// Phase H — Company Cash Hold ledger.
//
// Company-aggregate view of physical cash collected on IN-books bookings
// (VAT / RCT / Cash+Invoice) via Cash-* paid methods, pending bank
// lodgment. Sister to /my-cash (which is the collector-personal view).
// Simple Cash / off-the-books bookings never appear here by design.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Wallet, Banknote, CheckCircle2, AlertCircle, History as HistoryIcon } from "lucide-react";
import LedgerFullHistoryDialog from "@/components/LedgerFullHistoryDialog";

function fmt(n, cur = "EUR") {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat("en-IE", { style: "currency", currency: cur }).format(v);
  } catch {
    return `${cur} ${v.toFixed(2)}`;
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IE", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function CashHold() {
  const [tab, setTab] = useState("pending"); // "pending" | "lodged"
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ holding_total: 0, lodged_total: 0, holding_count: 0, lodged_count: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [lodgeOpen, setLodgeOpen] = useState(false);
  const [lodgeTarget, setLodgeTarget] = useState(null); // null = bulk, otherwise single row
  const [lodgeDate, setLodgeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [lodgeRef, setLodgeRef] = useState("");
  const [lodgeNotes, setLodgeNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Phase P.30 — full company cash-hold audit history modal.
  const [historyOpen, setHistoryOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: tot }] = await Promise.all([
        api.get("/cash-holdings", { params: { status: tab === "pending" ? "holding" : "lodged" } }),
        api.get("/cash-holdings/totals"),
      ]);
      setRows(Array.isArray(list) ? list : []);
      setTotals(tot || {});
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load Cash Hold");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openLodgeSingle = (row) => {
    setLodgeTarget(row);
    setLodgeDate(new Date().toISOString().slice(0, 10));
    setLodgeRef(""); setLodgeNotes("");
    setLodgeOpen(true);
  };
  const openLodgeBulk = () => {
    if (selected.size === 0) return;
    setLodgeTarget(null);
    setLodgeDate(new Date().toISOString().slice(0, 10));
    setLodgeRef(""); setLodgeNotes("");
    setLodgeOpen(true);
  };

  const submitLodge = async () => {
    setSubmitting(true);
    try {
      const lodged_at = lodgeDate ? new Date(`${lodgeDate}T12:00:00`).toISOString() : new Date().toISOString();
      if (lodgeTarget) {
        await api.post(`/cash-holdings/${lodgeTarget.id}/lodge`, {
          lodged_at, reference: lodgeRef || undefined, notes: lodgeNotes || undefined,
        });
        toast.success("Cash entry lodged");
      } else {
        const ids = Array.from(selected);
        const { data } = await api.post("/cash-holdings/bulk-lodge", {
          ids, lodged_at, reference: lodgeRef || undefined, notes: lodgeNotes || undefined,
        });
        const errCount = (data?.errors || []).length;
        if (errCount > 0) {
          toast.warning(`Lodged ${data.lodged} entries; ${errCount} skipped`);
        } else {
          toast.success(`Lodged ${data.lodged} entries`);
        }
      }
      setLodgeOpen(false);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to lodge");
    } finally {
      setSubmitting(false);
    }
  };

  const lodgedGrouped = useMemo(() => {
    if (tab !== "lodged") return [];
    // Group lodged rows by (lodged_at date + reference) for batch display.
    const buckets = new Map();
    for (const r of rows) {
      const dateKey = (r.lodged_at || "").slice(0, 10);
      const key = `${dateKey}::${r.lodged_reference || ""}`;
      if (!buckets.has(key)) {
        buckets.set(key, {
          key, lodged_at: r.lodged_at, lodged_reference: r.lodged_reference || "",
          lodged_by_name: r.lodged_by_name || "",
          lodged_notes: r.lodged_notes || "",
          entries: [], total: 0,
        });
      }
      const bucket = buckets.get(key);
      bucket.entries.push(r);
      bucket.total += Number(r.amount || 0);
    }
    return Array.from(buckets.values()).sort((a, b) => (b.lodged_at || "").localeCompare(a.lodged_at || ""));
  }, [tab, rows]);

  return (
    <div className="container max-w-6xl px-4 py-6 space-y-6" data-testid="cash-hold-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <Banknote className="text-emerald-600" size={24} /> Company Cash Hold
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Physical cash collected on in-books jobs (VAT / RCT / Cash+Invoice), pending bank lodgment.
            Simple Cash and off-the-books bookings stay off this ledger.
          </p>
        </div>
        <Button
          onClick={() => setHistoryOpen(true)}
          variant="outline"
          className="gap-2"
          data-testid="cash-hold-full-history-btn"
        >
          <HistoryIcon className="h-4 w-4" /> Full history
        </Button>
      </header>

      {/* Headline totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div
          className="md:col-span-2 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-[8px] p-5"
          data-testid="cash-hold-total-tile"
        >
          <div className="text-[11px] mono uppercase tracking-widest">Company is holding</div>
          <div className="text-4xl font-extrabold tabular-nums mono mt-1">
            {fmt(totals.holding_total)}
          </div>
          <div className="text-sm mt-1">
            {totals.holding_count || 0} entr{totals.holding_count === 1 ? "y" : "ies"} pending lodgment
          </div>
        </div>
        <div
          className="bg-slate-50 border border-slate-200 text-slate-900 rounded-[8px] p-5 flex flex-col justify-center"
          data-testid="cash-hold-lodged-pill"
        >
          <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground">Lodged (total)</div>
          <div className="text-xl font-extrabold tabular-nums mono mt-1">
            {fmt(totals.lodged_total)}
          </div>
          <div className="text-xs text-muted-foreground">
            {totals.lodged_count || 0} entr{totals.lodged_count === 1 ? "y" : "ies"}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-border">
        <button
          type="button"
          onClick={() => setTab("pending")}
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${tab === "pending" ? "border-emerald-600 text-emerald-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="cash-hold-tab-pending"
        >
          Pending lodgment {totals.holding_count > 0 ? <span className="ml-1 mono text-xs">({totals.holding_count})</span> : null}
        </button>
        <button
          type="button"
          onClick={() => setTab("lodged")}
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${tab === "lodged" ? "border-emerald-600 text-emerald-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="cash-hold-tab-lodged"
        >
          Lodged history {totals.lodged_count > 0 ? <span className="ml-1 mono text-xs">({totals.lodged_count})</span> : null}
        </button>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground" data-testid="cash-hold-loading">Loading…</div>
      )}

      {!loading && tab === "pending" && (
        <>
          {selected.size > 0 && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 text-amber-900 rounded-[6px] px-3 py-2">
              <div className="text-sm font-semibold">{selected.size} selected</div>
              <Button
                type="button"
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={openLodgeBulk}
                data-testid="cash-hold-bulk-lodge-btn"
              >
                <CheckCircle2 size={14} className="mr-1" /> Lodge selected
              </Button>
            </div>
          )}
          {rows.length === 0 ? (
            <div className="bg-card border border-border rounded-[8px] p-8 text-center text-sm text-muted-foreground" data-testid="cash-hold-empty-pending">
              No cash pending lodgment. Collect cash on a VAT or RCT booking via a Cash-* paid method and it&apos;ll land here.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                    <th className="px-3 py-2 w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" /></th>
                    <th className="px-3 py-2 text-left">Paid on</th>
                    <th className="px-3 py-2 text-left">Booking</th>
                    <th className="px-3 py-2 text-left">Customer</th>
                    <th className="px-3 py-2 text-left">Collector</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`cash-hold-row-${r.id}`}>
                      <td className="px-3 py-2"><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)} aria-label={`Select ${r.booking_code}`} /></td>
                      <td className="px-3 py-2 mono tabular-nums whitespace-nowrap">{fmtDate(r.paid_at)}</td>
                      <td className="px-3 py-2 mono text-[11px] uppercase tracking-widest">{r.booking_code}</td>
                      <td className="px-3 py-2 truncate max-w-[200px]">{r.customer_name || "—"}</td>
                      <td className="px-3 py-2 text-[12px]">{r.collector_name || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] uppercase">{r.payment_type}</Badge></td>
                      <td className="px-3 py-2 text-right font-extrabold tabular-nums mono">{fmt(r.amount, r.currency)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          type="button"
                          size="sm"
                          className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => openLodgeSingle(r)}
                          data-testid={`cash-hold-lodge-btn-${r.id}`}
                        >
                          Lodge
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!loading && tab === "lodged" && (
        <>
          {lodgedGrouped.length === 0 ? (
            <div className="bg-card border border-border rounded-[8px] p-8 text-center text-sm text-muted-foreground" data-testid="cash-hold-empty-lodged">
              No lodged entries yet.
            </div>
          ) : (
            <div className="space-y-3">
              {lodgedGrouped.map((g) => (
                <div key={g.key} className="bg-card border border-border rounded-[8px] p-4" data-testid={`cash-hold-lodged-batch-${g.key}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground">Lodged</div>
                      <div className="text-lg font-extrabold">{fmtDate(g.lodged_at)}</div>
                      {g.lodged_reference && <div className="text-xs mt-1">Ref: <span className="mono font-bold">{g.lodged_reference}</span></div>}
                      {g.lodged_by_name && <div className="text-[11px] text-muted-foreground">by {g.lodged_by_name}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-extrabold tabular-nums mono">{fmt(g.total)}</div>
                      <div className="text-[11px] text-muted-foreground">{g.entries.length} entr{g.entries.length === 1 ? "y" : "ies"}</div>
                    </div>
                  </div>
                  {g.lodged_notes && (
                    <div className="text-xs mt-2 italic text-muted-foreground border-l-2 border-emerald-300 pl-2">{g.lodged_notes}</div>
                  )}
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View entries</summary>
                    <ul className="mt-2 divide-y divide-border">
                      {g.entries.map((e) => (
                        <li key={e.id} className="py-1.5 flex items-center justify-between gap-3">
                          <span className="mono text-[11px] uppercase">{e.booking_code}</span>
                          <span className="flex-1 truncate text-[12px]">{e.customer_name}</span>
                          <span className="mono tabular-nums font-bold">{fmt(e.amount, e.currency)}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Lodge modal — single or bulk */}
      <Dialog open={lodgeOpen} onOpenChange={setLodgeOpen}>
        <DialogContent data-testid="cash-hold-lodge-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet size={18} /> Lodge cash to bank
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-sm">
              {lodgeTarget ? (
                <span>Lodging <span className="font-bold mono">{lodgeTarget.booking_code}</span> · {fmt(lodgeTarget.amount, lodgeTarget.currency)}</span>
              ) : (
                <span>Lodging <span className="font-bold">{selected.size}</span> selected entries</span>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-bold">Lodgment date</Label>
              <Input
                type="date"
                value={lodgeDate}
                onChange={(e) => setLodgeDate(e.target.value)}
                data-testid="cash-hold-lodge-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-bold">Reference (deposit slip, transaction ID…)</Label>
              <Input
                value={lodgeRef}
                onChange={(e) => setLodgeRef(e.target.value)}
                placeholder="DEPOSIT-001"
                data-testid="cash-hold-lodge-reference"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-bold">Notes</Label>
              <Textarea
                value={lodgeNotes}
                onChange={(e) => setLodgeNotes(e.target.value)}
                rows={2}
                placeholder="Optional"
                data-testid="cash-hold-lodge-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setLodgeOpen(false)} data-testid="cash-hold-lodge-cancel">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitLodge}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="cash-hold-lodge-confirm"
            >
              {submitting ? "Lodging…" : "Confirm lodgment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LedgerFullHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        variant="cash"
        ledgerLabel="Company Cash Hold"
      />
    </div>
  );
}
